import { TASK_WORKFLOW_STATUSES } from '@tasks/services/task-status.service';
import { Task, TaskView } from './tasks.types';
import { FieldDef } from '@core/models';
import { RelatedToType } from '@shared/enums';
import { IRowNode } from 'ag-grid-enterprise';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';

export namespace TaskUtils {
  export const mergeTaskPagesNested = (existing: Task[], newTasks: Task[]): Task[] => {
    const seen = new Map<number, number>();
    const merged = existing.map((t, i) => {
      if (t.id != null) seen.set(t.id, i);
      return { ...t, children: t.children ? [...t.children] : [] };
    });

    for (const task of newTasks) {
      if (task.id != null && seen.has(task.id)) {
        const idx = seen.get(task.id)!;
        const existing = merged[idx];
        const mergedChildren = mergeTaskPagesNested(existing.children ?? [], task.children ?? []);
        merged[idx] = { ...existing, ...task, children: mergedChildren };
      } else {
        if (task.id != null) seen.set(task.id, merged.length);
        merged.push({ ...task, children: task.children ? [...task.children] : [] });
      }
    }
    return merged;
  };

  export const mergeTaskPages = (existing: Task[], newTasks: Task[]): Task[] => {
    const seen = new Map<string, number>();
    const merged = existing.map((t, i) => {
      if (t.group_path_key) seen.set(t.group_path_key, i);
      return { ...t };
    });

    for (const task of newTasks) {
      const key = task.group_path_key;
      if (key && seen.has(key)) {
        const idx = seen.get(key)!;
        const existingTask = merged[idx];
        if (existingTask.task_type === 'section' && task.task_type === 'section') {
          const mergedChildren = [
            ...new Set([...(existingTask.child_group_path_keys ?? []), ...(task.child_group_path_keys ?? [])]),
          ];
          merged[idx] = { ...existingTask, child_group_path_keys: mergedChildren };
        } else {
          merged[idx] = { ...existingTask, ...task };
        }
      } else {
        if (key) seen.set(key, merged.length);
        merged.push({ ...task });
      }
    }
    return merged;
  };

  export const mergePartialTaskFields = (
    existing: Task[],
    partial: Task[],
    fields: string[],
    nested: boolean,
  ): Task[] => {
    const byId = new Map<number, Task>();
    for (const p of partial) {
      if (p?.id != null) byId.set(+p.id, p);
    }

    const apply = (tasks: Task[]): Task[] =>
      tasks.map((task) => {
        const match = task.id != null ? byId.get(+task.id) : undefined;
        let next = task;
        if (match) {
          const patch: Record<string, unknown> = {};
          for (const f of fields) patch[f] = (match as any)[f];
          next = { ...task, ...patch };
        }
        if (nested && next.children?.length) {
          next = { ...next, children: apply(next.children) };
        }
        return next;
      });

    return apply(existing);
  };

  export const getTaskListRelatedToTypeOptions = (
    customObjects: CustomObject[],
  ): { id: string; name: string; applicable_type?: string }[] => {
    return [
      { id: RelatedToType.Company, name: 'Company' },
      { id: RelatedToType.Contact, name: 'Contact' },
      { id: RelatedToType.Project, name: 'Deal' },
      { id: RelatedToType.Account, name: 'Global Task' },
      { id: RelatedToType.Property, name: 'Property' },
      ...customObjects.filter((co) => co.name.startsWith('co_')).map((co) => ({ id: co.name, name: co.label })),
    ];
  };

  export const deepFindTaskById = (tasks: Task[], parentId: number): Task | undefined => {
    for (const task of tasks) {
      if (task.id === parentId) {
        return task;
      }

      if (task.children) {
        const found = deepFindTaskById(task.children, parentId);
        if (found) {
          return found;
        }
      }
    }

    return undefined;
  };

  export const deepTaskDeleteById = (tasks: Task[], deletedTaskIds: number[]): Task[] => {
    return tasks.filter((task) => {
      if (deletedTaskIds.includes(task.id)) {
        return false;
      }

      if (task.children) {
        task.children = deepTaskDeleteById(task.children, deletedTaskIds);
      }

      return true;
    });
  };

  export const removeDuplicatedAndUnsearchedTasks = (tasks: Task[]): Task[] => {
    const searchedTaskIds = new Set();

    // Identify and remove tasks from root level if their parent_id exists in root
    const filteredTasks = tasks.filter((task) => {
      if (tasks.some((rootTask) => rootTask.id === task.parent_id)) {
        searchedTaskIds.add(task.id);
        return false;
      }
      return true;
    });

    filteredTasks.forEach((task) => {
      if (task.children) {
        task.children = task.children.filter((child) => searchedTaskIds.has(child.id));
      }
    });

    return filteredTasks;
  };

  export const deepUpdateTaskById = (tasks: Task[], updatedTask: Task): Task[] => {
    return tasks.map((task) => {
      if (task.id === updatedTask.id) {
        return { ...task, ...updatedTask };
      }

      if (task.children) {
        return { ...task, children: deepUpdateTaskById(task.children, updatedTask) };
      }

      return task;
    });
  };

  export const deepSortTasksByRank = (tasks: Task[]): Task[] => {
    tasks?.sort((a, b) => a.rank - b.rank);
    return (
      tasks?.map((task) => {
        if (task.children) {
          task.children?.sort((a, b) => a.rank - b.rank);
          deepSortTasksByRank(task.children);
        }
        return task;
      }) ?? []
    );
  };

  export const flattenTasks = (tasks: Task[]): Task[] => {
    return (
      tasks?.reduce((acc, task) => {
        if (task?.children) {
          acc?.push(task, ...flattenTasks(task?.children));
        } else {
          acc?.push(task);
        }
        return acc;
      }, [] as Task[]) ?? []
    );
  };

  export const groupTasksByStatus = (tasks: Task[]): { [key: string]: Task[] } => {
    let groupedTasksByStatus = {};

    return TASK_WORKFLOW_STATUSES.reduce((acc, worflow) => {
      acc[worflow.key] = tasks.filter((task) => task.workflow_state === worflow.key && task.task_type == 'task');
      return acc;
    }, groupedTasksByStatus);

    // for (let worflow of TASK_WORKFLOW_STATUSES) {
    //   const results = tasks.filter((task) => task.workflow_state == worflow.key && task.task_type == 'task');
    //   groupedTasksByStatus[worflow.key] = results;
    // }

    // return groupedTasksByStatus;
  };

  export const getTaskStatusLabel = (workflowKey: string): string => {
    const taskStatus = TASK_WORKFLOW_STATUSES.find((workflow) => workflow.key === workflowKey);
    return taskStatus?.label || workflowKey;
  };

  export const taskCustomFieldValuesAsAttributes = (task: Task): { [key: string]: any } => {
    const transformCfValues = (task: Task): { [key: string]: any } => {
      const attributes = task.custom_field_values?.reduce((acc, field) => {
        acc[field.name] = field.val;
        return acc;
      }, {});

      if (task.children) {
        attributes['children'] = task.children.map((tchild) => transformCfValues(tchild));
      }

      return { ...task, ...attributes };
    };

    return transformCfValues(task);
  };

  export const getTaskDeleteConfirmationMessage = (task: Task): string => {
    const childrenCount = task.children.length;

    if (task.task_type === 'section') {
      switch (task.children.length) {
        case 0:
          return `You are deleting a section which will delete all tasks in the section. Do you wish to proceed?`;
        case 1:
          return `You are deleting a section that will also <strong>delete ${childrenCount} task</strong> associated with it
            Do you wish to proceed?`;
        default:
          return `You are deleting a section that will also <strong>delete ${childrenCount} tasks</strong> associated with it.
            Do you wish to proceed?`;
      }
    } else {
      switch (childrenCount) {
        case 0:
          return `Are you sure you want to delete this task?`;
        case 1:
          return `You are deleting a task that will also <strong>delete ${childrenCount} subtask</strong>.
            Do you wish to proceed?`;
        default:
          return `You are deleting a task that will also <strong>delete ${childrenCount} subtasks</strong>.
            Do you wish to proceed?`;
      }
    }
  };

  export const getApplicableFields = (relatedTo: RelatedToType | string, fields: FieldDef[]): FieldDef[] => {
    let notApplicableFields = ['project_title', 'project_id', 'project_phase_id', 'project_stage_id'];

    if (relatedTo === RelatedToType.Project) {
      notApplicableFields = ['property_id', 'property_title'];
    } else if (
      relatedTo === RelatedToType.Account ||
      relatedTo === RelatedToType.CustomObjectValue ||
      relatedTo?.startsWith('co_')
    ) {
      notApplicableFields.push('assigned_task_role_id');
    } else if (relatedTo === RelatedToType.AggregatedTasks) {
      return fields;
    }

    return fields.filter((x) => !notApplicableFields.includes(x.name));
  };

  export const getEditStartDateConfirmationMessage = (task: Task, fieldLabel = 'start_date'): string => {
    const hasLinkedTo = task.linked_to && task.linked_to.length > 0;
    const hasPredecessors = task.predecessors && task.predecessors.length > 0;
    fieldLabel = fieldLabel.toLowerCase() === 'start_date' ? 'Start Date' : 'Due Date';

    if (hasLinkedTo && hasPredecessors) {
      return `Manually changing the '${fieldLabel}' will result in the removal 
      of all predecessor relationships and links. Do you wish to proceed?`;
    }

    if (hasLinkedTo) {
      return `Manually changing the '${fieldLabel}' will result in the removal of all links. Do you wish to proceed?`;
    }

    if (hasPredecessors) {
      return `Manually changing the '${fieldLabel}' will result in the removal of all predecessor relationships. Do you wish to proceed?`;
    }

    return '';
  };

  export const hasObjectChanged = (obj1: any, obj2: any): boolean => {
    // Check for strict equality
    if (obj1 === obj2) {
      return false; // No change if both are identical
    }

    // Check if either of them is not an object or is null
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
      return true; // Change if either is not an object
    }

    // Get all keys of both objects
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // If the number of keys is different, the objects are different
    if (keys1.length !== keys2.length) {
      return true;
    }

    // Check if all keys and values are the same
    for (const key of keys1) {
      // If obj2 doesn't have the same key or the values are different
      if (!keys2.includes(key) || hasObjectChanged(obj1[key], obj2[key])) {
        return true; // Return true if there is a change
      }
    }

    return false; // No change if no differences were found
  };

  export const generateExpandedRowKey = (node: IRowNode<Task>, taskView: TaskView): string => {
    return node.key;
  };

  /**
   * Editing any of these fields triggers cascade changes on the backend
   * example, changing a start_date may changes predecessors dates.
   */
  export const FIELDS_THAT_TRIGGER_BACKEND_CASCADE = new Set([
    'start_date',
    'due_date',
    'end_date',
    'completed_at',
    'predecessors',
    'predecessor_task_ids',
    'predecessor_codes',
    'predecessor_summary',
    'predecessor_tasks_count',
    'workflow_state',
    'parent_id',
  ]);

  export const TASK_IDENTIFIER_FIELDS = new Set(['id', 'task_list_id', 'task_type']);

  /**
   * Fields the backend can change ehind the scenes.
   * Always included in diffAndEmit even if not visible in taskview list table.
   * Used by `SyncCascadingChanges`.
   */
  export const FIELDS_THAT_REQUIRE_BACKEND_RESYNC = new Set([
    // ordering
    'rank',
    'task_section_task_rank',
    'task_list_section_rank',
    // hierarchy & grouping
    'task_section_id',
    'parent_id',
    'group_path',
    'group_path_key',
    'child_group_path_keys',
    'task_section_titles',
    // dates (cascade-recalculated)
    'start_date',
    'due_date',
    'end_date',
    'completed_at',
    // dependencies
    'predecessors',
    'predecessor_task_ids',
    'predecessor_codes',
    'predecessor_summary',
    'predecessor_tasks_count',
    // computed
    'percent_complete',
    'num_open_children',
    'num_closed_children',
    // state
    'workflow_state',
  ]);

  const GROUP_BY_FIELD_ALIASES: Record<string, string[]> = {
    task_section_id: ['task_section_titles', 'task_section_title'],
    // Has-many fields are grouped by their singular "fieldDef" name in the UI,
    // but updates are sent as *_ids arrays (and the UI often updates the related arrays too).
    approver_id: ['approver_ids', 'approvals', 'approver_names', 'approver_name'],
    contact_id: ['contact_ids', 'contacts', 'contact_names', 'contact_name'],
    following_user_id: ['following_user_ids', 'followings', 'following_user_names', 'following_user_name'],
  };

  export const getActiveGroupByFields = (groupBy: string | string[][]): Set<string> => {
    if (!groupBy) return new Set();
    const raw = typeof groupBy === 'string' ? (groupBy ? [groupBy] : []) : groupBy.map((g) => g[0]);
    const expanded: string[] = [];
    for (const f of raw) {
      expanded.push(f, ...(GROUP_BY_FIELD_ALIASES[f] ?? []));
    }
    return new Set(expanded);
  };

  export interface UpdateResyncStrategy {
    needsResync: boolean;
    groupByChanged: boolean;
    serverCascade: boolean;
  }

  export const classifyUpdateResync = (
    taskPayload: Record<string, any>,
    groupBy: string | string[][],
  ): UpdateResyncStrategy => {
    const changedFields = Object.keys(taskPayload).filter((k) => !TASK_IDENTIFIER_FIELDS.has(k));
    const activeGroupByFields = getActiveGroupByFields(groupBy);
    const serverCascade = changedFields.some((f) => FIELDS_THAT_TRIGGER_BACKEND_CASCADE.has(f));
    const groupByChanged = changedFields.some((f) => activeGroupByFields.has(f));
    return { needsResync: serverCascade || groupByChanged, groupByChanged, serverCascade };
  };

  export const needsFilterTasksAfterUpdate = (
    taskPayload: Record<string, any>,
    groupBy: string | string[][],
  ): boolean => classifyUpdateResync(taskPayload, groupBy).needsResync;

  export const buildPartialTaskPayload = (task: Pick<Task, 'id' | 'task_list_id'>, changes: Partial<Task>): Task => {
    return {
      id: task.id,
      task_list_id: task.task_list_id,
      ...changes,
    } as Task;
  };

  /** Returns keys where value differs between two objects. Objects/arrays compared by JSON.stringify; null/undefined treated as equal. */
  export const getDiffKeys = (oldObj: Record<string, unknown>, newObj: Record<string, unknown>): string[] => {
    const keys = [...new Set([...Object.keys(oldObj), ...Object.keys(newObj)])];
    return keys.filter((key) => {
      const oldVal = oldObj[key];
      const newVal = newObj[key];
      if (typeof oldVal === 'object' && oldVal !== null && typeof newVal === 'object' && newVal !== null) {
        return JSON.stringify(oldVal) !== JSON.stringify(newVal);
      }
      return (oldVal != null || newVal != null) && oldVal !== newVal;
    });
  };

  /** Returns an object with only the keys that differ, taking values from newValues. Uses getDiffKeys for comparison. */
  export const extractChangedFields = <T extends Record<string, unknown>>(
    newValues: T,
    reference: T,
  ): Partial<T> | null => {
    const keys = getDiffKeys(reference, newValues);
    if (keys.length === 0) return null;
    return keys.reduce((acc, key) => ({ ...acc, [key]: newValues[key] }), {} as Partial<T>);
  };
}
